import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowLeft, ArrowRight, ExternalLink, Github, Calendar, Tag, Layers, CheckCircle, ArrowUpRight, User } from 'lucide-react';
import BackToTopButton from '../components/BackToTopButton';
import BlurCarousel from '../components/ui/blur-carousel';

const img = (src, caption, description) => ({ src, caption, description });

const projectsData = {
  1: {
    title: "BS PARFUMS",
    subtitle: "Luxury Perfume Webshop",
    category: "E-COMMERCE DEVELOPMENT",
    year: "2024",
    client: "Web Development – Semester 1",
    duration: "2 Months",
    role: "Full-Stack Developer (Solo)",
    image: "https://images.unsplash.com/photo-1588514912908-8f5891714f8d?q=80&w=1169&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    color: "from-purple-500 to-pink-600",
    description: "BS Parfums is a luxurious interactive webshop where visitors can discover and purchase exclusive fragrances. Built entirely solo, from design to deployment, with an elegant dark aesthetic and smooth animations.",
    challenge: "Creating a complete full-stack webshop independently, managing both front-end and back-end while maintaining a consistent luxury feel throughout.",
    solution: "HTML, CSS and JavaScript for the elegant front-end. PHP, Laravel and Livewire for the back-end with dynamic components and seamless interactions without full page reloads.",
    contribution: "This is a fully solo project, from concept to deployment. I designed the complete layout, built the front-end with HTML, CSS and JavaScript, and developed the back-end with Laravel and Livewire. All features were implemented by me: the product catalogue, shopping cart, checkout flow and admin dashboard for product management.",
    technologies: ["HTML", "CSS", "JavaScript", "PHP", "Laravel", "Livewire", "MySQL"],
    features: [
      "Responsive and stylish product catalog",
      "Elegant dark theme with animations",
      "Shopping cart and checkout flow",
      "Admin dashboard for product management",
      "Call-to-action hero section"
    ],
    images: [
      img("../images/home_bsparfums.png", "Home Page", "The luxurious landing page featuring a hero section with a strong call-to-action, elegant dark colour palette and smooth entrance animations, fully designed and built solo."),
      img("../images/home_2.png", "Products Section", "Elegant product display with cards, imagery and pricing, styled with custom CSS animations and a dark luxury aesthetic."),
      img("../images/10_bsparfums_2.png", "Perfume Collection", "Full collection overview with category filtering and an immersive browsing experience for discovering exclusive fragrances."),
      img("../images/detail_bsparfums.png", "Product Detail Page", "Detailed product page with fragrance description, pricing, imagery and a direct add-to-cart button, built with Livewire for seamless interaction."),
    ],
    liveUrl: "#",
    githubUrl: "#",
    nextProject: 2,
    prevProject: 9
  },
  2: {
    title: "APPIES LEGOBIB",
    subtitle: "LEGO Library for Hospitalised Children",
    category: "FULL-STACK APPLICATION",
    year: "2025",
    client: "Skills Integration – Semester 2",
    duration: "3 Months",
    role: "Full-Stack Developer",
    image: "https://images.unsplash.com/photo-1587654780291-39c9404d746b?w=1400&h=800&fit=crop",
    color: "from-yellow-500 to-orange-500",
    description: "Appie's LEGO Bib brings joy and distraction to hospitalised children through a LEGO lending library. The platform lets children on the paediatric ward reserve LEGO sets, while donors can contribute new sets. Built with Angular and .NET in an NX monorepo with CI/CD on Azure.",
    challenge: "Building a production-ready full-stack application with a modern monorepo architecture, CI/CD pipeline and a deployment on Azure, while keeping the user experience warm and child-friendly.",
    solution: "NX monorepo with Angular frontend and .NET backend. Azure deployment via CI/CD pipeline. Component-driven development approach with a clear separation between the reservation and donation flows.",
    contribution: "Within this team project I built the home page, implemented the login screen, developed the shared navbar and footer as reusable components, created the contact page, and completed several essential CRUD modules for managing LEGO sets and reservations.",
    technologies: ["Angular", ".NET", "NX Monorepo", "Azure", "CI/CD", "TypeScript"],
    features: [
      "LEGO reservation system for hospitalised children",
      "Donation flow for LEGO sets",
      "Sponsor overview section",
      "CI/CD pipeline with Azure deployment",
      "Component-driven architecture with NX monorepo"
    ],
    images: [
      img("../images/appies-legobib-app-admin.png", "Admin Dashboard", "Management panel for admins to oversee LEGO sets, users and reservations through a structured overview dashboard."),
      img("../images/desktop_admin_login.png", "Login Screen", "Authentication screen for admins and staff, clean and minimal design with secure login flow."),
      img("../images/admin-gebruikers.png", "User Management", "CRUD interface for managing users, admins can create, edit and remove accounts with role-based access."),
      img("../images/reserveren_1.png", "Reservation Flow – Step 1", "First step of the reservation flow, children browse and select their desired LEGO set from a clear catalogue."),
      img("../images/reserveren_2.png", "Reservation Flow – Step 2", "Second step of the reservation flow, confirmation of the chosen set with date and location details."),
      img("../images/reservatie.png", "My Reservations", "Overview of active and past reservations per user, with status indicators and management actions."),
    ],
    liveUrl: "https://appieslegobib-app-client-dev-evf8bhb4ceetbpes.uksouth-01.azurewebsites.net/",
    githubUrl: "#",
    nextProject: 3,
    prevProject: 1
  },
  3: {
    title: "MOTOCONNECT",
    subtitle: "Mobile App Concept for Motorcycle Riders",
    category: "MOBILE APP DESIGN",
    year: "2024",
    client: "Digital Startup – Semester 1",
    duration: "1.5 Months",
    role: "UX/UI Designer & Business Concept",
    image: "https://images.unsplash.com/photo-1552306062-29a5560e1c31?q=80&w=2070&auto=format&fit=crop",
    color: "from-orange-500 to-red-600",
    description: "MotoConnect is a mobile app concept where motorcycle riders can connect, track rides and manage group trips. Developed and pitched as a startup idea for the Digital Startup course in semester 1.",
    challenge: "Identifying a gap in the market for motorcycle riders and translating it into a compelling startup pitch for a professional business audience.",
    solution: "A professional pitch with a visual prototype in Figma, including complete branding with a distinctive moving ribbon/curve identity, animations and clear target group positioning.",
    contribution: "I contributed to developing and applying the business concepts to the Figma designs. Team member Yenthe had already partially worked out the initial idea, and I helped refine the UX flow and visual design. Our pitch earned 2nd place at the jury for the Digital Startup course.",
    technologies: ["Figma", "UI/UX Design", "Prototyping", "Branding", "Market Research"],
    features: [
      "Ride tracking and route management",
      "Group ride organisation",
      "Community and social features",
      "Strong visual brand identity (moving ribbon/curve)",
      "Professional startup pitch deck"
    ],
    images: [
      img("../images/motoconnect (1).png", "App Concept Overview", "The overall MotoConnect app concept, connecting motorcycle riders through a sleek, purpose-built interface designed in Figma."),
      img("../images/motoconnect (2).png", "Ride Tracking", "Live ride tracking with route display and per-trip statistics. Designed as an interactive Figma prototype."),
      img("../images/motoconnect (3).png", "Group Rides", "Group ride management, plan and organise rides with friends, including a participation overview and real-time updates."),
      img("../images/motoconnect (4).png", "Community Feed", "Social feed where riders can share experiences, photos and recommended routes with the community."),
      img("../images/motoconnect (5).png", "Visual Branding", "The MotoConnect brand identity featuring the distinctive moving ribbon/curve motif and strong typographic system."),
      img("../images/motoconnect (6).png", "Startup Pitch", "The professional pitch deck presented to the jury for the Digital Startup course, we placed 2nd with this concept."),
      img("../images/motoconnect (7).png", "Figma Design System", "Component-based Figma designs with a full design system, auto-layout and variant management."),
      img("../images/motoconnect (8).png", "Profile Page", "User profile showing ride statistics, followed routes and motorcycle details, clean and data-driven layout."),
    ],
    liveUrl: "https://www.motoconnect.be/",
    githubUrl: "#",
    nextProject: 4,
    prevProject: 2
  },
  4: {
    title: "AMAZON REDESIGN",
    subtitle: "Component-Driven UI Redesign in Figma",
    category: "UX/UI DESIGN",
    year: "2025",
    client: "School Project – Semester 2",
    duration: "1.5 Months",
    role: "UI/UX Designer",
    image: "https://images.unsplash.com/photo-1523474253046-8cd2748b5fd2?w=1400&h=800&fit=crop",
    color: "from-blue-500 to-cyan-500",
    description: "A Figma redesign of Amazon's interface using a fully component-driven design approach. The project focused on building reusable design systems, improving visual hierarchy and modernising the user interface through structured component libraries.",
    challenge: "Amazon's interface is dense and complex. The challenge was to redesign it with a clean, component-driven methodology without losing the functionality that users rely on.",
    solution: "A structured Figma project with a full component library, buttons, cards, navbars, product grids and more, all built to be reusable and scalable. Each screen was assembled from components rather than drawn from scratch.",
    contribution: "Each team member redesigned one page, I handled the home page. I also designed the navbar and footer as shared components for the rest of the team, and set up the component library and grid system that served as the foundation for the entire project.",
    technologies: ["Figma", "Component Design", "Design System", "Auto Layout", "Variants"],
    features: [
      "Full component library with variants",
      "Redesigned product cards and navigation",
      "Consistent spacing and typography system",
      "Auto layout and responsive frames",
      "Modern visual aesthetic with clear hierarchy"
    ],
    images: [
      img("../images/amazon (1).png", "Home Page Redesign", "My page contribution, a fully redesigned Amazon home page with improved visual hierarchy, modern product cards and a cleaner hero section."),
      img("../images/amazon (2).png", "Navigation & Layout", "Redesigned global navbar with structured category navigation, search bar and account controls, built as a shared component for the team."),
      img("../images/amazon (3).png", "Product Grid", "Product display section with component-based cards, badge system and consistent pricing layout, assembled entirely from the reusable component library."),
    ],
    liveUrl: "#",
    githubUrl: "#",
    nextProject: 5,
    prevProject: 3
  },
  5: {
    title: "AR RACING",
    subtitle: "Augmented Reality Car Racing App",
    category: "MOBILE APP",
    year: "2025",
    client: "School Project – Semester 2",
    duration: "2 Months",
    role: "Mobile Developer",
    image: "https://images.unsplash.com/photo-1724908267988-63310819325c?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    color: "from-purple-500 to-pink-600",
    description: "An augmented reality mobile app where you race miniature cars in the real world using your phone camera. Built with Flutter for the cross-platform mobile shell and Unity for the AR experience.",
    challenge: "Combining Flutter and Unity in a single app while keeping the AR performance smooth and the racing experience engaging on both iOS and Android.",
    solution: "Flutter as the app shell and UI layer, Unity as the embedded AR view for real-time 3D car racing. AR Foundation handles plane detection so cars appear to drive on real surfaces.",
    contribution: "I built the entire Flutter mobile app from scratch, from the UI and navigation to the login flow with Firebase Authentication and all CRUD operations via Firestore. This includes the home screen, profile screen, admin module, favourites logic and the integration with the Unity AR module.",
    technologies: ["Flutter", "Unity", "AR Foundation", "Dart", "C#", "Firebase"],
    features: [
      "Augmented reality car racing on real surfaces",
      "Cross-platform: iOS and Android",
      "Unity-powered 3D rendering inside Flutter",
      "Plane detection with AR Foundation",
      "Multiple car models and race tracks"
    ],
    images: [
      img("../images/homeAR.png", "Home Screen", "The main menu of the AR Racing app, built in Flutter with Firebase Authentication and smooth transitions between screens."),
      img("../images/gameAR.png", "AR Game View", "The augmented reality race view, Unity 3D rendering embedded inside Flutter with real-time plane detection on physical surfaces."),
      img("../images/lockscreenAR (1).png", "Login Screen", "Login and lock screen with animations, connected to Firebase Auth for secure user authentication."),
      img("../images/favoriteAR.png", "Favourites", "Save favourite cars and tracks, full CRUD functionality implemented via Firestore database."),
      img("../images/profielAR.png", "Profile Page", "User profile with race statistics, avatar selection and account management, fully built in Flutter."),
      img("../images/adminpaneelAR.png", "Admin Panel", "Administration panel for adding and removing car models and race tracks, protected by role-based access."),
      img("../images/deleteAR.png", "Delete Functionality", "Item removal with confirmation dialogue, part of the complete CRUD implementation across the app."),
      img("../images/pcAR.png", "Desktop View", "Desktop-compatible interface view of the app for demonstration and development purposes."),
    ],
    liveUrl: "#",
    githubUrl: "#",
    nextProject: 6,
    prevProject: 4
  },
  6: {
    title: "FRIDGECHEF",
    subtitle: "Smart Fridge & Recipe Manager",
    category: "FULL-STACK APPLICATION",
    year: "2025",
    client: "School Project",
    duration: "3-4 Weeks",
    role: "Full-Stack Developer",
    image: "https://images.unsplash.com/photo-1606859191214-25806e8e2423?q=80&w=1135&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    color: "from-green-500 to-teal-500",
    description: "FridgeChef helps you manage your fridge contents and suggests recipes based on what you already have. Add ingredients manually or by scanning, track expiry dates and get recipe ideas so nothing goes to waste.",
    challenge: "Making ingredient tracking feel effortless and turning a simple inventory list into genuinely useful meal inspiration.",
    solution: "A clean React interface connected to a recipe API. Users manage their fridge contents and the app filters available recipes in real time based on what's there. Expiry tracking adds urgency and reduces food waste.",
    contribution: "Although this was a group project, I handled the majority of both the back-end and front-end. I designed the database structure, set up the API endpoints and built the React interface, including the scan functionality, ingredient management, AI integration and recipe suggestions.",
    technologies: ["React", "Node.js", "REST API", "MySQL", "Tailwind CSS"],
    features: [
      "Fridge inventory management",
      "Recipe suggestions based on available ingredients",
      "Expiry date tracking with alerts",
      "Search and filter by cuisine or diet",
      "Shopping list generator for missing items"
    ],
    images: [
      img("../images/fridge_chef_home.png", "Home Screen", "Fridge contents overview with colour-coded expiry dates, quickly see what needs to be used first."),
      img("../images/fridge_chef_Add.png", "Add Ingredient", "Form for manually adding ingredients with name, category and expiry date, clean and minimal input flow."),
      img("../images/fridge_chef_scan.png", "Barcode Scanner", "Built-in barcode scanning feature to automatically add products to the fridge inventory."),
      img("../images/fridge_chef_scan_found.png", "Product Recognised", "Auto-populated product information after a successful barcode scan, name, category and nutritional data."),
      img("../images/fridge_chef_add_ai.png", "AI Ingredient Recognition", "AI-powered feature that identifies ingredients from a description and suggests matching database entries."),
      img("../images/fridge_chef_gegenereerd.png", "Generated Recipe", "Recipe generated based on the available ingredients currently in the fridge, reduces food waste."),
      img("../images/fridge_chef_gegenereerd_1.png", "Recipe Detail", "Detailed recipe view with step-by-step instructions, full ingredient list and estimated cooking time."),
    ],
    liveUrl: "#",
    githubUrl: "#",
    nextProject: 7,
    prevProject: 5
  },
  7: {
    title: "DUFFALO FC",
    subtitle: "Football Club Management Platform",
    category: "FULL-STACK APPLICATION",
    year: "2025",
    client: "Skills Integration 2 – Semester 2",
    duration: "2 Months",
    role: "Full-Stack Developer",
    image: "https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=1400&h=800&fit=crop",
    color: "from-green-500 to-emerald-600",
    description: "Duffalo FC is a full-stack website for a fictional football club with match info, player statistics and club management. The platform serves both fans and players with dynamic filters and colour-coded match indicators.",
    challenge: "Building a full-stack application with dynamic content and an effective team workflow, while keeping the UI user-friendly and data-driven.",
    solution: "Laravel backend with Livewire for reactive components. Full CRUD for matches and players. Tailwind CSS for a responsive, interactive interface. Clear task division in the team led to efficient collaboration.",
    contribution: "Within the Duffalo FC team project I built the following: the matches overview tab displaying all upcoming and completed fixtures, the player and user profile page, the full site layout including navbar and footer, the home page, and several CRUD modules for managing match locations and shirt sizes. The website is also live online.",
    technologies: ["Laravel", "Livewire", "Tailwind CSS", "MySQL", "PHP"],
    features: [
      "Match calendar with dynamic filters and colour indicators",
      "Player profiles and statistics",
      "CRUD management for matches and players",
      "User-friendly UI with interactive elements",
      "Frontend and backend fully integrated"
    ],
    images: [
      img("../images/duffalo (1).png", "Home Page", "The Duffalo FC homepage, hero section with club atmosphere, upcoming fixture teaser and clean dark layout."),
      img("../images/duffalo (2).png", "Home Overview", "Full home page overview including the welcome section, latest news and a quick match summary widget."),
      img("../images/duffalo (3).png", "Matches Overview Tab", "The main matches tab showing all upcoming and completed fixtures, one of my primary contributions to the project."),
      img("../images/duffalo (4).png", "Upcoming Fixtures", "Filtered view of upcoming matches with date, opponent, venue and kick-off time, colour-coded by status."),
      img("../images/duffalo (5).png", "Match Results", "Overview of completed matches with final scores and win/draw/loss colour indicators for quick scanning."),
      img("../images/duffalo (6).png", "Match Filter", "Dynamic filter controls allowing users to sort and filter matches by date, competition type or result."),
      img("../images/duffalo (7).png", "Match Detail", "Detailed match page with full team lineup, kick-off time, venue information and match statistics."),
      img("../images/duffalo (8).png", "Match Score View", "Score display with goalscorers, match timeline and post-match statistics per player."),
      img("../images/duffalo (9).png", "Profile Page", "Player profile page displaying personal info, position, jersey number and club statistics, built by me."),
      img("../images/duffalo (10).png", "Player Statistics", "Detailed statistics panel showing goals, assists, appearances and performance trends per player."),
      img("../images/duffalo (11).png", "Profile Settings", "Profile edit form allowing players to update their personal details, avatar and contact information."),
      img("../images/duffalo (12).png", "Navbar & Layout", "The global navigation bar and page layout structure, built as reusable Blade components in Laravel with mobile responsiveness."),
      img("../images/duffalo (13).png", "Footer", "The site footer with navigation links, club contact details and social media links, part of my layout contribution."),
      img("../images/duffalo (14).png", "Locations CRUD", "Management interface for match venues, full CRUD allowing admins to add, edit and remove locations."),
      img("../images/duffalo (15).png", "Shirt Sizes Management", "CRUD panel for club shirt sizes, part of the member and merchandise management system."),
      img("../images/duffalo (16).png", "Admin Dashboard", "Administrative overview panel with quick access to all management modules for club admins."),
      img("../images/duffalo (17).png", "Players Overview", "Full squad view with player cards showing names, positions and jersey numbers in a responsive grid layout."),
      img("../images/duffalo (18).png", "Add Player", "Form for registering a new player to the club with position, jersey number and contact details."),
      img("../images/duffalo (19).png", "Mobile View", "Fully responsive mobile version of the Duffalo FC website, optimised for all screen sizes."),
      img("../images/duffalo (20).png", "Responsive Design", "Cross-device layout overview demonstrating the site's responsiveness across mobile, tablet and desktop screens."),
    ],
    liveUrl: "https://de.duffalos.be/",
    githubUrl: "#",
    nextProject: 9,
    prevProject: 6
  },
  9: {
    title: "WISHMAP",
    subtitle: "Encrypted Birthday Wishlist Web App",
    category: "FULL-STACK APPLICATION",
    year: "2026",
    client: "Personal Project",
    duration: "1+ Month (Ongoing)",
    role: "Full-Stack Developer & UX Designer",
    image: "https://images.unsplash.com/photo-1516799946559-464579a81a7a?q=80&w=1113&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    color: "from-yellow-400 to-amber-500",
    description: "WishMap is a birthday wishlist web app where the birthday person can create a wish list and share it with guests via an encrypted link. Guests can see and claim gifts in an interactive mind map, the birthday person stays completely surprised because all claims are encrypted and invisible to them.",
    challenge: "Birthday gift coordination is chaotic, people buy duplicates, miss what someone actually wants, or spoil the surprise by asking directly. There was no simple tool that keeps the birthday person in the dark while letting guests coordinate openly.",
    solution: "A dark, minimal web app built with Vue 3 featuring an SVG mind map visualisation and end-to-end encryption. The guest link contains the decryption key in the URL fragment, meaning it is never sent to any server and the owner cannot see claims even by inspecting localStorage.",
    contribution: "WishMap is a fully solo project, from concept to technical implementation. I built the interactive SVG mind map, implemented the end-to-end encryption via the Web Crypto API and developed the complete Vue 3 application without any backend. The design, UX flow and all code were created by me.",
    technologies: ["Vue 3", "JavaScript", "Web Crypto API", "CSS3", "localStorage", "SVG"],
    features: [
      "Interactive SVG mind map with animated nodes",
      "End-to-end encrypted claims, birthday person sees nothing",
      "Shareable guest link with key in URL fragment (never hits a server)",
      "Claim coordination, guests see who claimed what, no duplicates",
      "Black and white design with gold accent",
      "No backend required, runs fully in the browser"
    ],
    images: [
      img("../images/wishmap.png", "Mind Map Overview", "The interactive SVG mind map with all wishes from the birthday person, animated nodes with connecting lines and a gold accent palette."),
      img("../images/wishmap (1).png", "Add a Wish", "Form for adding a new wish to the mind map, name, description, link and optional price."),
      img("../images/wishmap (2).png", "Claim a Gift", "Guests can claim a wish via the shared link, the claim is encrypted so the birthday person can never see it."),
      img("../images/wishmap (3).png", "Guest View", "The guest-facing view with visible claims, coordination is easy and duplicates are impossible."),
      img("../images/wishmap (4).png", "Wish Detail", "Expanded wish detail with description, external link and claim status, clean modal over the mind map."),
      img("../images/wishmap (5).png", "Share Link", "Generating the shareable guest link, the AES-256-GCM decryption key is embedded in the URL fragment and never reaches any server."),
      img("../images/wishmap (6).png", "Mobile Design", "Responsive mobile layout of WishMap with the signature black-and-white gold colour palette."),
    ],
    liveUrl: "#",
    githubUrl: "#",
    nextProject: 1,
    prevProject: 7
  }
};


function ProgressBar() {
  const { scrollYProgress } = useScroll();
  return (
    <motion.div
      style={{ scaleX: scrollYProgress }}
      className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 origin-left z-[100]"
    />
  );
}

function InfoCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-5 border border-white/10 hover:border-orange-500/50 transition-all group relative">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center group-hover:bg-orange-500/30 transition-colors">
          <Icon className="w-5 h-5 text-orange-500 group-hover:scale-110 transition-transform" />
        </div>
        <div>
          <p className="text-white/40 text-xs tracking-widest font-silkscreen">{label}</p>
          <p className="text-white font-bold">{value}</p>
        </div>
      </div>
    </div>
  );
}


function ProjectDetail() {
  const { id } = useParams();
  const project = projectsData[id];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 500], [0, 150]);
  const heroOpacity = useTransform(scrollY, [0, 300], [1, 0.3]);

  if (!project) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <h1 className="text-8xl font-bold text-white/10 font-silkscreen mb-4">404</h1>
            <h2 className="text-3xl font-bold text-white mb-4 font-silkscreen">PROJECT NOT FOUND</h2>
            <p className="text-white/50 mb-8">The project you're looking for doesn't exist.</p>
            <Link to="/projects">
              <button className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-full font-bold font-silkscreen transition-colors">
                BACK TO PROJECTS
              </button>
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  const nextProject = projectsData[project.nextProject];
  const prevProject = projectsData[project.prevProject];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="bg-[#0a0a0a] text-white min-h-screen"
    >
      <ProgressBar />

      <Link to="/projects">
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="fixed top-24 sm:top-28 left-4 sm:left-6 z-50 bg-white/10 backdrop-blur-xl p-3 sm:p-4 rounded-full border border-white/20 transition-all group"
        >
          <ArrowLeft className="w-4 sm:w-5 h-4 sm:h-5 text-white" />
        </motion.button>
      </Link>

      <section className="relative min-h-screen flex items-end overflow-hidden">
        <motion.div
          style={{ y: heroY, opacity: heroOpacity }}
          className="absolute inset-0"
        >
          <img
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/60 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a]/80 via-transparent to-transparent" />
        </motion.div>

        <div className="relative z-10 w-full max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-12 pb-12 sm:pb-16 lg:pb-24 pt-24 sm:pt-32">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-end">
            <div>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="flex flex-wrap items-center gap-3 sm:gap-4 mb-4 sm:mb-6"
              >
                <span className="bg-white/10 backdrop-blur-xl px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border border-white/20 text-xs sm:text-sm font-silkscreen">
                  {project.year}
                </span>
                <span className="text-orange-500 text-xs sm:text-sm tracking-widest font-silkscreen">
                  {project.category}
                </span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl leading-[0.9] mb-3 sm:mb-4 font-silkscreen"
              >
                {project.title}
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-base sm:text-xl lg:text-2xl text-white/70 mb-6 sm:mb-8"
              >
                {project.subtitle}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="flex flex-wrap gap-3 sm:gap-4"
              >
                {project.liveUrl && project.liveUrl !== "#" && (
                  <a href={project.liveUrl} target="_blank" rel="noopener noreferrer">
                    <button className="group flex items-center gap-2 sm:gap-3 bg-orange-500 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-full font-bold transition-all font-silkscreen text-xs sm:text-sm">
                      <ExternalLink className="w-3 sm:w-4 h-3 sm:h-4" />
                      LIVE DEMO
                      <ArrowUpRight className="w-3 sm:w-4 h-3 sm:h-4 group-hover:rotate-45 transition-transform" />
                    </button>
                  </a>
                )}
                {project.githubUrl && project.githubUrl !== "#" && (
                  <a href={project.githubUrl} target="_blank" rel="noopener noreferrer">
                    <button className="group flex items-center gap-2 sm:gap-3 bg-white/10 backdrop-blur-xl text-white px-4 sm:px-6 py-2 sm:py-3 rounded-full font-bold transition-all border border-white/20 font-silkscreen text-xs sm:text-sm">
                      <Github className="w-3 sm:w-4 h-3 sm:h-4" />
                      SOURCE CODE
                    </button>
                  </a>
                )}
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
              className="grid grid-cols-2 gap-3 sm:gap-4"
            >
              <InfoCard icon={Calendar} label="YEAR" value={project.year} />
              <InfoCard icon={Tag} label="CLIENT" value={project.client} />
              <InfoCard icon={Layers} label="DURATION" value={project.duration} />
              <InfoCard icon={CheckCircle} label="ROLE" value={project.role} />
            </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="flex flex-col items-center gap-2 text-white/30"
          >
            <span className="text-xs tracking-widest font-silkscreen">SCROLL</span>
            <div className="w-px h-8 bg-gradient-to-b from-white/30 to-transparent" />
          </motion.div>
        </motion.div>
      </section>

      {/* Overview / Challenge / Solution */}
      <section className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 lg:px-12 overflow-hidden">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid lg:grid-cols-3 gap-12 lg:gap-24">
            <div className="lg:col-span-2 space-y-16">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <span className="text-orange-500 text-sm tracking-widest font-silkscreen mb-4 block">
                  01 OVERVIEW
                </span>
                <h2 className="text-2xl lg:text-3xl font-bold mb-6 font-silkscreen">
                  PROJECT DESCRIPTION
                </h2>
                <p className="text-white/60 text-lg leading-relaxed">
                  {project.description}
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <span className="text-orange-500 text-sm tracking-widest font-silkscreen mb-4 block">
                  02 CHALLENGE
                </span>
                <h2 className="text-2xl lg:text-3xl font-bold mb-6 font-silkscreen">
                  THE PROBLEM
                </h2>
                <p className="text-white/60 text-lg leading-relaxed">
                  {project.challenge}
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <span className="text-orange-500 text-sm tracking-widest font-silkscreen mb-4 block">
                  03 SOLUTION
                </span>
                <h2 className="text-2xl lg:text-3xl font-bold mb-6 font-silkscreen">
                  THE APPROACH
                </h2>
                <p className="text-white/60 text-lg leading-relaxed">
                  {project.solution}
                </p>
              </motion.div>
            </div>

            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="sticky top-32 space-y-8"
              >
                <div className="bg-[#151515] rounded-[32px] p-8 border border-white/5">
                  <h3 className="text-lg font-bold mb-6 font-silkscreen text-orange-500">
                    TECHNOLOGIES
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {project.technologies.map((tech, idx) => (
                      <span
                        key={idx}
                        className="bg-white/5 text-white/80 px-4 py-2 rounded-full text-sm border border-white/10 transition-colors"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="bg-[#151515] rounded-[32px] p-8 border border-white/5">
                  <h3 className="text-lg font-bold mb-6 font-silkscreen text-orange-500">
                    KEY FEATURES
                  </h3>
                  <ul className="space-y-4">
                    {project.features.map((feature, idx) => (
                      <motion.li
                        key={idx}
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: idx * 0.1 }}
                        className="flex items-start gap-3"
                      >
                        <div className="w-2 h-2 rounded-full bg-orange-500 mt-2 flex-shrink-0" />
                        <span className="text-white/70">{feature}</span>
                      </motion.li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* My Contribution */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-12 bg-[#0d0d0d]">
        <div className="max-w-[1400px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-orange-500 text-sm tracking-widest font-silkscreen mb-4 block">
              04 MY CONTRIBUTION
            </span>
            <h2 className="text-2xl lg:text-3xl font-bold mb-8 font-silkscreen">
              MY ROLE IN THIS PROJECT
            </h2>
            <div className="grid lg:grid-cols-3 gap-8 lg:gap-16 items-start">
              <div className="lg:col-span-2">
                <div className="relative pl-5 border-l-2 border-orange-500/50">
                  <div className="absolute -left-[5px] top-1 w-2.5 h-2.5 rounded-full bg-orange-500" />
                  <p className="text-white/65 text-lg leading-relaxed">
                    {project.contribution}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4 bg-white/3 border border-white/8 rounded-2xl p-6">
                <div className="w-10 h-10 bg-orange-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-orange-500" />
                </div>
                <div>
                  <p className="text-white/40 text-xs tracking-widest font-silkscreen mb-1">ROLE</p>
                  <p className="text-white font-bold text-sm">{project.role}</p>
                  <p className="text-white/40 text-xs mt-3 tracking-widest font-silkscreen mb-1">DURATION</p>
                  <p className="text-white font-bold text-sm">{project.duration}</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-24 lg:py-32 px-6 lg:px-12 bg-[#0f0f0f]">
        <div className="max-w-[1800px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-orange-500 text-sm tracking-widest font-silkscreen mb-4 block">
              05 GALLERY
            </span>
            <h2 className="text-4xl lg:text-5xl font-bold font-silkscreen">
              PROJECT IMAGES
            </h2>
            <p className="text-white/40 text-sm mt-4">Click any image to view full screen</p>
          </motion.div>

          {project.images && project.images.length > 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <BlurCarousel images={project.images} />
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex flex-col items-center justify-center py-16 gap-4"
            >
              <div className="w-16 h-16 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                <svg className="w-7 h-7 text-white/20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <p className="text-white/30 text-sm font-silkscreen tracking-widest">NO IMAGES AVAILABLE FOR THIS PROJECT</p>
            </motion.div>
          )}
        </div>
      </section>

      {/* More Projects */}
      <section className="py-24 lg:py-32 px-6 lg:px-12 border-t border-white/5">
        <div className="max-w-[1800px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-orange-500 text-sm tracking-widest font-silkscreen mb-4 block">
              CONTINUE EXPLORING
            </span>
            <h2 className="text-4xl lg:text-5xl font-bold font-silkscreen">
              MORE PROJECTS
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            <Link to={`/projects/${project.prevProject}`}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.02 }}
                className="group relative h-[300px] rounded-[32px] overflow-hidden cursor-pointer"
              >
                <img
                  src={prevProject?.image}
                  alt={prevProject?.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/70 to-transparent" />
                <div className="absolute inset-0 p-8 flex flex-col justify-between">
                  <div className="flex items-center gap-2 text-white/50">
                    <ArrowLeft className="w-4 h-4" />
                    <span className="text-xs font-silkscreen">PREVIOUS PROJECT</span>
                  </div>
                  <div>
                    <span className="text-orange-500 text-xs font-silkscreen block mb-2">
                      {prevProject?.category}
                    </span>
                    <h3 className="text-2xl lg:text-3xl font-bold font-silkscreen transition-colors">
                      {prevProject?.title}
                    </h3>
                  </div>
                </div>
              </motion.div>
            </Link>

            <Link to={`/projects/${project.nextProject}`}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.02 }}
                className="group relative h-[300px] rounded-[32px] overflow-hidden cursor-pointer"
              >
                <img
                  src={nextProject?.image}
                  alt={nextProject?.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-l from-[#0a0a0a] via-[#0a0a0a]/70 to-transparent" />
                <div className="absolute inset-0 p-8 flex flex-col justify-between items-end text-right">
                  <div className="flex items-center gap-2 text-white/50">
                    <span className="text-xs font-silkscreen">NEXT PROJECT</span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="text-orange-500 text-xs font-silkscreen block mb-2">
                      {nextProject?.category}
                    </span>
                    <h3 className="text-2xl lg:text-3xl font-silkscreen transition-colors">
                      {nextProject?.title}
                    </h3>
                  </div>
                </div>
              </motion.div>
            </Link>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link to="/projects">
              <button className="group bg-white/5 border border-white/10 text-white px-8 py-4 rounded-full font-bold transition-all font-silkscreen text-sm flex items-center gap-3 mx-auto">
                VIEW ALL PROJECTS
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="relative py-8 sm:py-12 overflow-hidden bg-gradient-to-b from-[#0a0a0a] to-black">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [0.03, 0.06, 0.03] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -top-20 left-1/4 w-96 h-96 bg-orange-500 rounded-full blur-3xl"
          />
          <motion.div
            animate={{ scale: [1, 1.3, 1], opacity: [0.02, 0.05, 0.02] }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 2 }}
            className="absolute -bottom-20 right-1/4 w-96 h-96 bg-purple-500 rounded-full blur-3xl"
          />
        </div>

        <div className="relative">
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.5, ease: "easeOut" }}
            className="h-px bg-gradient-to-r from-transparent via-orange-500/50 to-transparent mb-6 sm:mb-8"
          />

          <div className="overflow-hidden">
            <motion.div
              animate={{ x: ["-50%", "0%"] }}
              transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
              className="flex items-center gap-12 sm:gap-16 whitespace-nowrap"
            >
              {[...Array(10)].map((_, i) => (
                <React.Fragment key={i}>
                  <div className="flex items-center gap-3 sm:gap-4">
                    <motion.span
                      className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-silkscreen bg-gradient-to-r from-white via-orange-500 to-white bg-clip-text text-transparent font-normal"
                      whileHover={{ scale: 1.05 }}
                      transition={{ type: "spring", stiffness: 400 }}
                      style={{ backgroundSize: '200% auto' }}
                    >
                      BEKIRHAN SIMSEK
                    </motion.span>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                      className="w-2 h-2 sm:w-3 sm:h-3 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full shadow-lg shadow-orange-500/50"
                    />
                  </div>
                </React.Fragment>
              ))}
            </motion.div>
          </div>

          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.5, ease: "easeOut", delay: 0.2 }}
            className="h-px bg-gradient-to-r from-transparent via-orange-500/50 to-transparent mt-6 sm:mt-8"
          />
        </div>
      </section>

      <BackToTopButton />
    </motion.div>
  );
}

export default ProjectDetail;
